import { Button, Portal, Spinner, Text } from '@chakra-ui/react';
import { FormField } from '@ui/components/FormField';
import { useUpdateUserProfile } from '@ui/hooks/api/useUser';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';

import { CloseButton } from '../CloseButton';
import { DialogOrDrawer } from '../DialogOrDrawer';
import { useSetUsernameModal } from './useSetUsernameModal';

interface FormDataType {
  name: string;
}

export const SetUsernameModal = () => {
  const {
    isOpen,
    username,
    closeModal,
    setUsername,
    title,
    ctaText,
    callbackOnComplete,
  } = useSetUsernameModal();

  const { mutateAsync: updateUserProfile, isLoading } = useUpdateUserProfile();
  const methods = useForm<FormDataType>({
    defaultValues: {
      name: username,
    },
    mode: 'all',
  });

  const handleSubmit: SubmitHandler<FormDataType> = async ({ name }) => {
    await updateUserProfile({ name });
    setUsername(name);
    callbackOnComplete?.(name);
    closeModal();
  };

  return (
    <DialogOrDrawer.Root
      open={isOpen}
      placement={{
        desktopDown: 'bottom',
        desktop: 'top',
      }}
      onOpenChange={(state) => !state.open && closeModal()}
    >
      <Portal>
        <DialogOrDrawer.Backdrop />
        <DialogOrDrawer.Positioner>
          <FormProvider {...methods}>
            <DialogOrDrawer.DragContent
              as="form"
              minH={{
                desktopDown: '70vh',
              }}
              onSubmit={methods.handleSubmit(handleSubmit)}
            >
              <DialogOrDrawer.CloseTrigger asChild>
                <CloseButton />
              </DialogOrDrawer.CloseTrigger>
              <DialogOrDrawer.Header>
                {title ?? 'Log in / Sign up'}
              </DialogOrDrawer.Header>
              <DialogOrDrawer.Body>
                <Text textStyle="body2" mb="24px" textAlign="center">
                  Fill your nickname! (◍•ᴗ•◍)✧*。
                </Text>
                <FormField
                  validators={{
                    maxLength: { value: 36, message: 'Maximum 36 characters' },
                    required: 'Nickname is required',
                  }}
                  name="name"
                  label="Nickname"
                  placeholder="Nickname"
                />
              </DialogOrDrawer.Body>
              <DialogOrDrawer.Footer>
                <Button
                  w="full"
                  type="submit"
                  disabled={isLoading || !methods.formState.isValid}
                >
                  {isLoading ? (
                    <Spinner size="sm" color="currentcolor" />
                  ) : (
                    ctaText ?? 'Sign up!'
                  )}
                </Button>
              </DialogOrDrawer.Footer>
            </DialogOrDrawer.DragContent>
          </FormProvider>
        </DialogOrDrawer.Positioner>
      </Portal>
    </DialogOrDrawer.Root>
  );
};
